import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Chip, TableSortLabel, Typography } from "@mui/material";
import {
  typography,
  spacing,
  colors,
} from "../../../../../../../../../assets/styles/tokens";

type Order = "asc" | "desc";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  // Helper to extract numeric value from formatted strings like "$1,234" or "45.0%"
  const extractNumber = (val: any): number => {
    if (typeof val === "string") {
      const cleanVal = val.replace(/[^0-9.-]+/g, "");
      const num = parseFloat(cleanVal);
      return isNaN(num) ? 0 : num;
    }
    return 0;
  };

  const valA = extractNumber(a[orderBy]);
  const valB = extractNumber(b[orderBy]);

  if (valB < valA) {
    return -1;
  }
  if (valB > valA) {
    return 1;
  }
  // Fallback to string comparison if not numeric
  if (String(b[orderBy]) < String(a[orderBy])) {
    return -1;
  }
  if (String(b[orderBy]) > String(a[orderBy])) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number,
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

type RrType = "success" | "warning" | "error";
export interface RevenueRow {
  cron_id: string;
  rrType: RrType;
  metric: string;
  actual_target: string;
  RR: string;
  PRR: string;
}
interface Props {
  TotalIncomeTableHeadings: { [key: string]: string };
  TotalIncomeTableRows: RevenueRow[];
}

const rrStyles = {
  success: {
    bg: colors.Success[50].$value,
    color: colors.Success[700].$value,
  },
  warning: {
    bg: colors.Warning[50].$value,
    color: colors.Warning[600].$value,
  },
  error: {
    bg: colors.Error[50].$value,
    color: colors.Error[600].$value,
  },
};

const PlayerValueTable = ({
  TotalIncomeTableHeadings,
  TotalIncomeTableRows,
}: Props) => {
  const headCells = Object.entries(TotalIncomeTableHeadings) as [
    keyof RevenueRow,
    string,
  ][];

  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof RevenueRow>("metric");

  const handleRequestSort = (
    _: React.MouseEvent<unknown>,
    property: keyof RevenueRow,
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedRows = React.useMemo(
    () => stableSort(TotalIncomeTableRows, getComparator(order, orderBy)),
    [TotalIncomeTableRows, order, orderBy],
  );

  return (
    <>
      <Table size="small">
        <TableHead sx={{ background: colors.GrayLightMode[100].$value }}>
          <TableRow
            sx={{
              // Hide all icons by default
              "& .MuiTableSortLabel-icon": {
                display: "none",
              },
              // Show icon only for first column
              "& th:first-of-type .MuiTableSortLabel-icon": {
                display: "inline-block",
              },
              "& th:first-of-type": {
                textAlign: "center",
              },
            }}
          >
            {headCells.map(([_, value], index) => (
              <TableCell
                key={index}
                align="left"
                sortDirection={orderBy === headCells[index][0] ? order : false}
                sx={{
                  whiteSpace: "nowrap",
                  fontSize: typography.Size.Text.xs.$value,
                  fontWeight: typography.Font_weight.Regular.$value,
                  paddingX: spacing.Spacing.xs.$value,
                  paddingY: spacing.Spacing.sm.$value,
                  lineHeight: typography.Line_height.text.xs.$value,
                  color: colors.GrayLightMode[900].$value,
                }}
              >
                <TableSortLabel
                  active={orderBy === headCells[index][0]}
                  direction={orderBy === headCells[index][0] ? order : "asc"}
                  onClick={(event) =>
                    handleRequestSort(event, headCells[index][0])
                  }
                >
                  {value && typeof value === "string" ? value : ""}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row) => (
            <React.Fragment key={row.cron_id}>
              <TableRow
                sx={{
                  "& > *": {
                    borderBottom:
                      "1px solid " +
                      colors.GrayLightMode[200].$value +
                      "!important",
                  },
                }}
              >
                {headCells.map(([key], cellIndex) => (
                  <TableCell
                    key={cellIndex}
                    align="left"
                    sx={{
                      // whiteSpace: "nowrap",
                      fontSize: typography.Size.Text.xs.$value,
                      fontWeight: typography.Font_weight.Regular.$value,
                      paddingX: spacing.Spacing.xs.$value,
                      paddingY: spacing.Spacing.xs.$value,
                      color: colors.GrayLightMode[900].$value,
                    }}
                  >
                    {key === "metric" ? (
                      <>
                        <Typography
                          sx={{
                            fontSize: typography.Size.Text.xs.$value,
                          }}
                        >
                          {" "}
                          {row[key] ? row[key] : "N/A"}
                        </Typography>
                      </>
                    ) : key === "actual_target" ? (
                      <Typography
                        sx={{
                          fontWeight: typography.Font_weight.Semibold.$value,
                          fontSize: typography.Size.Text.xs.$value,
                        }}
                      >
                        {" "}
                        {row[key] ? row[key] : "N/A"}
                      </Typography>
                    ) : key === "RR" ? (
                      <Chip
                        label={row[key] ? row[key] : "N/A"}
                        size="small"
                        sx={{
                          bgcolor: rrStyles[row.rrType]?.bg,
                          color: rrStyles[row.rrType]?.color,
                          fontWeight: typography.Font_weight.Semibold.$value,
                          borderRadius: spacing.Radius.sm.$value,
                          lineHeight: typography.Line_height.text.xs.$value,
                          fontSize: typography.Size.Text.xs.$value,
                        }}
                      />
                    ) : key === "PRR" ? (
                      <Typography
                        sx={{
                          fontWeight: typography.Font_weight.Semibold.$value,
                          fontSize: typography.Size.Text.xs.$value,
                        }}
                      >
                        {" "}
                        {row[key] ? row[key] : "N/A"}
                      </Typography>
                    ) : (
                      "N/A"
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

export default PlayerValueTable;
